import React from "react";
import { useState, useRef, useEffect } from "react";
import {
    Box, Typography, Avatar, Chip, Button, IconButton, Grid, TextField,
    Stack, Popper, Grow, Paper, ClickAwayListener, Tabs, Tab, Menu, MenuItem, Collapse
} from "@mui/material";
import moment from "moment";
import TableComponent from "components/grid";
import StatusBadge from "components/badge/StatusBadge";
import CustomPagination from "components/CustomPagination";
import { ReactComponent as EditIcon } from "assets/icons/EditIcon.svg";
import { LuEye, LuRefreshCw, LuSearch } from "react-icons/lu";
import { BiListUl } from "react-icons/bi";
import { MdOutlineFileDownload, MdOutlineFilterAlt } from "react-icons/md";
import { FaPlus } from "react-icons/fa6";
import Select from "react-select";
import CustomButton from "components/Button";
import { useParams } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ViewApproval from "./viewApproval";
import AddApproval from "./addApproval";
import EditApproval from "./editApproval";
import {
    useLazyGetAllStatutoryApprovalQuery,
    useLazyGetSingleStatutoryApprovalDetailsQuery,
    useGetAllStatutoryApprovalCountQuery,
    useGetAllStatutoryApprovalCategoryCountQuery,
    useGetAllStatutoryApprovalDurationCountQuery,
    useLazyGetExportAllStatutoryApprovalQuery
} from "store/slices/apis/statutoryApprovalSlice";
import CategoryCount from "./CategoryCountChart";
import CountByMonthChart from "./CountByMonthChart";
import { useGetAllContactsQuery } from "store/slices/apis/contactSlice";
import { useGetMasterDataQuery } from "store/slices/apis/masterConfigation";
import { mapAndSortAlphabetically } from "utils/mapSortHelper";
import { usePermission } from "hooks/usePermission";
import { STATUTORY_APPROVAL_CREATE, STATUTORY_APPROVAL_EDIT, STATUTORY_APPROVAL_VIEW } from "helpers/permissionConfig";
import ProjectLastUpdatedOn from "components/ProjectLastUpdatedOn";
import TrackerPageHeader from "components/TrackerPageHeader";
import { useGetLastUpdatedOnStatusQuery } from "store/slices/apis/projectStatusApiSlice";
import { DEFAULT_DATE_FORMAT } from "config";
import useExcelExport from "hooks/useExcelExport";

const calenderSVG = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_894_610)">
            <path d="M12.6667 1.33333H12V0.666667C12 0.489856 11.9298 0.320286 11.8047 0.195262C11.6797 0.0702379 11.5101 0 11.3333 0C11.1565 0 10.987 0.0702379 10.8619 0.195262C10.7369 0.320286 10.6667 0.489856 10.6667 0.666667V1.33333H5.33333V0.666667C5.33333 0.489856 5.2631 0.320286 5.13807 0.195262C5.01305 0.0702379 4.84348 0 4.66667 0C4.48986 0 4.32029 0.0702379 4.19526 0.195262C4.07024 0.320286 4 0.489856 4 0.666667V1.33333H3.33333C2.4496 1.33439 1.60237 1.68592 0.97748 2.31081C0.352588 2.93571 0.00105857 3.78294 0 4.66667L0 12.6667C0.00105857 13.5504 0.352588 14.3976 0.97748 15.0225C1.60237 15.6474 2.4496 15.9989 3.33333 16H12.6667C13.5504 15.9989 14.3976 15.6474 15.0225 15.0225C15.6474 14.3976 15.9989 13.5504 16 12.6667V4.66667C15.9989 3.78294 15.6474 2.93571 15.0225 2.31081C14.3976 1.68592 13.5504 1.33439 12.6667 1.33333ZM1.33333 4.66667C1.33333 4.13623 1.54405 3.62753 1.91912 3.25245C2.29419 2.87738 2.8029 2.66667 3.33333 2.66667H12.6667C13.1971 2.66667 13.7058 2.87738 14.0809 3.25245C14.456 3.62753 14.6667 4.13623 14.6667 4.66667V5.33333H1.33333V4.66667ZM12.6667 14.6667H3.33333C2.8029 14.6667 2.29419 14.456 1.91912 14.0809C1.54405 13.7058 1.33333 13.1971 1.33333 12.6667V6.66667H14.6667V12.6667C14.6667 13.1971 14.456 13.7058 14.0809 14.0809C13.7058 14.456 13.1971 14.6667 12.6667 14.6667Z" fill="#433F5E" />
            <path d="M8 11C8.55228 11 9 10.5523 9 10C9 9.44772 8.55228 9 8 9C7.44772 9 7 9.44772 7 10C7 10.5523 7.44772 11 8 11Z" fill="#433F5E" />
            <path d="M4.66656 11C5.21885 11 5.66656 10.5523 5.66656 10C5.66656 9.44772 5.21885 9 4.66656 9C4.11428 9 3.66656 9.44772 3.66656 10C3.66656 10.5523 4.11428 11 4.66656 11Z" fill="#433F5E" />
            <path d="M11.3334 11C11.8857 11 12.3334 10.5523 12.3334 10C12.3334 9.44772 11.8857 9 11.3334 9C10.7811 9 10.3334 9.44772 10.3334 10C10.3334 10.5523 10.7811 11 11.3334 11Z" fill="#433F5E" />
        </g>
        <defs>
            <clipPath id="clip0_894_610">
                <rect width="16" height="16" fill="white" />
            </clipPath>
        </defs>
    </svg>
);


const ApprovalTracker = () => {

    const { projectId } = useParams();

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortOrder, setSortOrder] = useState('desc');
    const [sortBy, setSortBy] = useState('createdDate');

    const editAccess = usePermission(STATUTORY_APPROVAL_EDIT);
    const viewAccess = usePermission(STATUTORY_APPROVAL_VIEW);
    const createAccess = usePermission(STATUTORY_APPROVAL_CREATE);

    const [open, setOpen] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const anchorRef = useRef(null);
    const [isAddApproval, setIsAddApproval] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedData, setSelectedData] = useState(null);
    const [isView, setIsView] = useState(false);
    const [viewData, setViewData] = useState(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [showSummary, setShowSummary] = useState(true);



    const [counts, setCounts] = useState({
        active: 0,
        inProgress: 0,
        completed: 0
    });
    const [trigger, { data: approvalList, isFetching }] = useLazyGetAllStatutoryApprovalQuery()

    const [getApprovalById, { data: approvalByIdData, isLoading: isapprovalByIdLoading }] = useLazyGetSingleStatutoryApprovalDetailsQuery();
    const { data: countData, refetch: refetchCounts } = useGetAllStatutoryApprovalCountQuery({ projectId: projectId, });

    // const { data: categoryCountData, refetch: refetchCategoryCount } =
    //     useGetAllStatutoryApprovalCategoryCountQuery(
    //         projectId
    //     );
    const { data: durationCountData, refetch: refetchdurationCount } =
        useGetAllStatutoryApprovalDurationCountQuery(
            projectId
        );
    const { data: contactsData, isLoading, error } = useGetAllContactsQuery();

    const module = "StatutoryApproval";

    const { data: statutoryApprovalStatusRes } = useGetMasterDataQuery({ module: module, category: 'Status', ProjectId: projectId });
    const statutoryApprovalStatusData = statutoryApprovalStatusRes?.data || [];
    const statusOptions = (statutoryApprovalStatusData || []).map(p => ({
        value: p?.value,
        label: p?.value,
    }));

    const { data: statutoryApprovalPhaseRes } = useGetMasterDataQuery({ module: module, category: 'Phase', ProjectId: projectId });
    const statutoryApprovalPhaseData = statutoryApprovalPhaseRes?.data || [];
    const phaseOptions = mapAndSortAlphabetically(statutoryApprovalPhaseData);

    const { data: statutoryApprovalSPOCRes } = useGetMasterDataQuery({ module: module, category: 'SPOC', ProjectId: projectId });
    const statutoryApprovalSPOCData = statutoryApprovalSPOCRes?.data || [];
    const spocOptions = mapAndSortAlphabetically(statutoryApprovalSPOCData);

    const { data: statutoryApprovalCategoryRes } = useGetMasterDataQuery({ module: module, category: 'Category', ProjectId: projectId });
    const statutoryApprovalCategoryData = statutoryApprovalCategoryRes?.data || [];
    const categoryOptions = mapAndSortAlphabetically(statutoryApprovalCategoryData);

    const { data: statusDate } = useGetLastUpdatedOnStatusQuery({ ProjectId: projectId });

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm !== filters.SearchTerm) {
                const updatedFilters = {
                    ...filters,
                    SearchTerm: searchTerm,
                    PageNumber: 1,
                };
                setFilters(updatedFilters);
                trigger(updatedFilters);
                setPageNumber(1);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const contactListRes = React.useMemo(() => {
        if (!contactsData?.data) return [];

        return mapAndSortAlphabetically(contactsData.data, "name", "email");

    }, [contactsData]);


    const handleMenuOpen = (event, row) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setMenuRow(row);
    };


    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuRow(null);
    };


    const handleMenuView = () => {
        if (menuRow) {
            handleViewClick(menuRow);
        }
        handleMenuClose();
    };


    const handleMenuEdit = () => {
        if (menuRow) {
            handleEditRisk(menuRow);
        }
        handleMenuClose();
    };


    const [filters, setFilters] = useState({
        SortBy: sortBy,
        SortOrder: sortOrder,
        PageNumber: pageNumber,
        PageSize: pageSize,
        ProjectId: projectId,
        SearchTerm: ""
    });

const { handleExport, isExporting } = useExcelExport({ exportHook: useLazyGetExportAllStatutoryApprovalQuery, fileName: "Statutory_Approval_Tracker_Data" });

    const exportStatutoryApprovalData = (rows) =>
        rows.map((row, index) => ({
            "S.No": index + 1,
            "ID": row.customId || "-",
            "Phase": row.phase || "-",
            "Permit/Approval/Agreement": row.permitApprovalAgreement || "-",
            "Category": row.statutoryTrackerType || "-",
            "Status": row.status || "-",
            "Permitting Entity": row.permittingEntity || "-",
            "Validity": row.validity && moment(row.validity).isValid()
                ? moment(row.validity).format(DEFAULT_DATE_FORMAT)
                : '-',
            "Details": row.details || "-",
            "Timelines": row.timelines && moment(row.timelines).isValid()
                ? moment(row.timelines).format(DEFAULT_DATE_FORMAT)
                : '-',
            "SPOC": row.spoc || "-",
            "Target timelines for attaining the permit": row.targetDate && moment(row.targetDate).isValid()
                ? moment(row.targetDate).format(DEFAULT_DATE_FORMAT)
                : '-',
            "Responsibility": row.responsibility || "-",
        }));

    useEffect(() => {
        trigger(filters);
    }, []);


    const handlePageChange = (newPage) => {
        setPageNumber(newPage);
        const updatedFilters = {
            ...filters,
            PageNumber: newPage,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };


    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPageNumber(1);
        const updatedFilters = {
            ...filters,
            PageSize: newSize,
            PageNumber: 1,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };


    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);

        const updatedFilters = {
            ...filters,
            PageNumber: 1,
            ...(newValue > 0 ? { Status: statusOptions[newValue - 1]?.value } : {}),
        };

        if (newValue === 0 && updatedFilters.Status) {
            delete updatedFilters.Status;
        }

        setFilters(updatedFilters);
        trigger(updatedFilters);
        setPageNumber(1);
    };


    const handleApplyFilters = () => {
        const updatedFilters = {
            ...filters,
            PageNumber: 1,
        };

        setFilters(updatedFilters);
        trigger(updatedFilters);
        setPageNumber(1);
        handleClose();
    };


    const handleReset = () => {
        const resetFilters = {
            ProjectId: projectId,
            SortBy: sortBy,
            SortOrder: sortOrder,
            PageNumber: 1,
            PageSize: 10,
        };
        setFilters(resetFilters);
        trigger(resetFilters);
        setPageNumber(1);
        handleClose();
    };


    const handleSelectChange = (selectedOption, fieldName) => {
        const updatedFilters = {
            ...filters,
            [fieldName]: selectedOption?.value || "",
            PageNumber: 1,
        };

        setFilters(updatedFilters);
        // trigger(updatedFilters);
        // setPageNumber(1);
    };


    const handleDateChange = (e) => {
        const { name, value } = e.target;

        let utcDate = "";
        if (value) {

            const localDate = new Date(value);
            utcDate = localDate.toISOString();
        }

        const updatedFilters = {
            ...filters,
            [name]: utcDate,
            PageNumber: 1,
        };

        setFilters(updatedFilters);
        // trigger(updatedFilters);
        // setPageNumber(1);
    };


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const updatedFilters = {
            ...filters,
            [name]: value,
            PageNumber: 1,
        };

        setFilters(updatedFilters);
        trigger(updatedFilters);
        setPageNumber(1);
    };



    const handleRequestSort = (property) => {
        const isAsc = sortBy === property && sortOrder === 'asc';
        const updatedFilters = {
            ...filters,
            SortBy: property,
            SortOrder: isAsc ? 'desc' : 'asc'
        };
        setFilters(updatedFilters);
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortBy(property);
        trigger(updatedFilters);
    };


    const handleDelete = (filterType) => {
        const updatedFilters = {
            ...filters,
            [filterType]: "",
            PageNumber: 1
        };

        setFilters(updatedFilters);
        trigger(updatedFilters);
        setTabValue(0)
        setPageNumber(1);
    };


    const handleAddSApproval = () => {
        setIsAddApproval((prev) => !prev);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleEditRisk = async (row) => {
        try {

            const result = await getApprovalById({
                statutoryTrackerId: row.statutoryTrackerId
            }).unwrap();
            setSelectedData(result.data || row);
            setIsEdit((prev) => !prev);

        } catch (error) {

            console.error('Error fetching design by ID:', error);
            setIsEdit((prev) => !prev);
            setSelectedData(row);
        }
    };


    const handleEditRiskClose = () => {
        setIsEdit((prev) => !prev);
        setSelectedData(null);
    };

    const handleViewClick = async (row) => {
        try {
            const result = await getApprovalById({
                statutoryTrackerId: row.statutoryTrackerId
            }).unwrap();

            setViewData(result?.data || row);
            setIsView(true);

        } catch (error) {
            setViewData(row);
            setIsView(true);
        }
    };


    const handleViewClose = () => {
        setIsView(false);
        setViewData(null);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };


    const getAvatarColor = (title) => {
        const colors = ["#1976D2", "#388E3C", "#F57C00", "#7B1FA2", "#0288D1", "#C2185B"];
        const index = title ? title.charCodeAt(0) % colors.length : 0;
        return colors[index];
    };

    const getCount = (value) => {

        return countData?.data?.statusCounts?.[value] ?? 0;
    };


    const highlightText = (text, highlight) => {
        if (!highlight || !text) return text;

        const regex = new RegExp(`(${highlight})`, 'gi');
        const parts = String(text).split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <span
                    key={index}
                    style={{
                        backgroundColor: '#FFF3CD',
                        color: '#856404',
                        fontWeight: 'bold',
                        padding: '0 2px',
                        borderRadius: '3px'
                    }}
                >
                    {part}
                </span>
            ) : part
        );
    };

    const handleToggleSummary = () => {
        setShowSummary((prev) => !prev);
    };


    const columns = [
        {
            name: "",
            field: "actions",
            width: "10px",
            sticky: true,
            renderer: (row) => (
                <>
                    {(viewAccess || editAccess) &&
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, row)}
                            >
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    }
                </>
            ),
        },
        {
            name: "ID",
            field: "customId",
            width: "70px",
            sticky: true,
        },
        {
            name: "Phase",
            field: "phase",
            width: "140px",
            sticky: true,
        },

        {
            name: "Permit/Approval/Agreement",
            field: "permitApprovalAgreement",
            width: "200px",
            sticky: true,
            renderer: (row) => highlightText(row.permitApprovalAgreement, searchTerm),
        },
        {
            name: 'Category',
            field: 'statutoryTrackerType',

        },
        {
            name: "Status",
            field: "status",
            renderer: (row) => (
                <StatusBadge status={row.status} />
            )
        },
        {
            name: 'Permitting Entity',
            field: 'permittingEntity',
            width: "200px"

        },
        {
            name: "Validity",
            field: "validity",
            renderer: (row) => (
                row?.validity ? moment(row.validity).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Details",
            field: "details",
            width: "200px"
        },
        {
            name: "Timelines",
            field: "timelines",
            renderer: (row) => (
                row?.timelines ? moment(row.timelines).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "SPOC",
            field: "spoc",
            width: "180px",
        },

        {
            name: "Target timelines for attaining the permit",
            field: "targetDate",
            renderer: (row) => (
                row?.targetDate ? moment(row.targetDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Responsibility",
            field: "responsibility",
        },
    ];


    return (
        <>
            <TrackerPageHeader title="Statutory Approval Tracker" description="Monitor approvals, validity, ownership, and due-date exposure.">
                {statusDate?.statutoryApprovals && <ProjectLastUpdatedOn date={statusDate?.statutoryApprovals} />}
            </TrackerPageHeader>
            <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid>
                    <Button
                        variant="text"
                        onClick={handleToggleSummary}
                        sx={{
                            fontFamily: "Outfit, sans-serif",
                            textTransform: "none",
                            fontWeight: 500,
                        }}
                    >
                        {showSummary ? "Hide Charts" : "Show Charts"}
                    </Button>
                </Grid>
                <Grid size={12}>
                    <Collapse in={showSummary} timeout="auto" unmountOnExit>
                        <Grid container spacing={2}>
                            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                                <CategoryCount columns={columns} />
                            </Grid>
                            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                                <CountByMonthChart data={durationCountData} columns={columns} />
                            </Grid>

                        </Grid>
                    </Collapse>
                </Grid>

                <Grid size={12}>
                    <Box
                        sx={{
                            width: "100%",
                            borderBottom: "2px solid #ECECEC",
                            overflowX: "auto",
                            whiteSpace: "nowrap",
                            p: 0,
                            display: "flex", alignItems: "center", justifyContent: 'space-between',
                            "-webkit-overflow-scrolling": "touch",
                        }}
                    >
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            TabIndicatorProps={{ style: { display: "none" } }}
                            variant="scrollable"
                            scrollButtons={false}
                            sx={{
                                justifyContent: "flex-start !important",
                                alignItems: "flex-start",
                                "& .MuiTabs-flexContainer": {
                                    justifyContent: "flex-start !important",
                                    gap: "30px",
                                },
                            }}
                        >
                            {["All", "Not Started", "Completed", "In Progress"].map((label, index) => {

                                return (
                                    <Tab
                                        key="0"
                                        label={
                                            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: "14px",
                                                        lineHeight: "100%",
                                                        color: tabValue === index ? "#0E73F6" : "#7E7E7E",
                                                        fontFamily: "Outfit, sans-serif"
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                                {index != 0 &&
                                                    <Chip
                                                        label={getCount(label)}
                                                        sx={{
                                                            height: "20px",
                                                            fontSize: "14px",
                                                            fontWeight: 500,
                                                            lineHeight: 1,
                                                            backgroundColor: "#E5E9EB",
                                                            color: "#5F5F5F",
                                                        }}
                                                    />
                                                }

                                            </Box>
                                        }
                                        sx={{
                                            textTransform: "none",
                                            minWidth: "auto",
                                            paddingBottom: "14px",
                                            paddingLeft: 0,
                                            paddingRight: 0,
                                            borderBottom: tabValue === index ? "2px solid #0E73F6" : "2px solid transparent",
                                        }}
                                    />
                                );
                            })}
                        </Tabs>
                        {createAccess &&
                            <CustomButton
                                label="Create Approval"
                                startIcon={<FaPlus />}
                                color="primary"
                                onClick={handleAddSApproval}
                                sx={{
                                    backgroundColor: "#2563EB",
                                    fontFamily: 'Outfit',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    lineHeight: '100%',
                                    letterSpacing: '0px',
                                }}
                            />
                        }

                    </Box>
                </Grid>


                <Grid size={12}>
                    <Grid container>
                        <Grid size={{ md: 8, sm: 6 }}>
                            <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                sx={{
                                    padding: "8px 0",
                                    alignItems: "center",
                                    minHeight: "40px"
                                }}
                            >
                                {filters.Phase && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Phase:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.Phase}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('Phase')}
                                    />
                                )}


                                {filters.StatutoryTrackerType && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Category:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.StatutoryTrackerType}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('StatutoryTrackerType')}
                                    />
                                )}

                                {filters.SPOC && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>SPOC:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.SPOC}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('SPOC')}
                                    />
                                )}

                                {filters.Status && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Status:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.Status}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('Status')}
                                    />
                                )}
                                {filters.Responsibility && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Responsibility:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.Responsibility}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('Responsibility')}
                                    />
                                )}

                                {filters.FromDate && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>From TargetDate:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {moment(filters.FromDate).format('DD-MM-YYYY')}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('FromDate')}
                                    />
                                )}
                                {filters.ToDate && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>To TargetDate:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {moment(filters.FromDate).format('DD-MM-YYYY')}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('ToDate')}
                                    />
                                )}
                            </Stack>
                        </Grid>


                        <Grid size={{ md: 4, sm: 6 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: 'end',
                                    p: "8px 0px"
                                }}
                            >
                                <TextField
                                    placeholder="Search by Permit/Approval/Agreement"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        startAdornment: <LuSearch style={{ marginRight: '8px', color: '#6B7280' }} />,
                                    }}
                                    sx={{
                                        minWidth: '250px',
                                        width: "100%",
                                        backgroundColor: '#fff',
                                        '& .MuiOutlinedInput-root': { borderRadius: '3px', minHeight: '36px', fontSize: '13px' },
                                        '& .MuiInputBase-input::placeholder': {
                                            fontSize: '0.9rem',
                                        }
                                    }}
                                    type="search"
                                />
                                <IconButton
                                    ref={anchorRef}
                                    onClick={handleToggle}
                                >
                                    <MdOutlineFilterAlt size={24} color="#48535B" />
                                </IconButton>


                                <Popper
                                    open={open}
                                    anchorEl={anchorRef.current}
                                    placement="bottom-end"
                                    transition
                                    disablePortal={false}
                                    sx={{ zIndex: 2000 }}
                                >
                                    {({ TransitionProps }) => (
                                        <Grow {...TransitionProps}>
                                            <Paper
                                                sx={{
                                                    mt: 1,
                                                    p: 2,
                                                    borderRadius: 1,
                                                    boxShadow: "0px 4px 20px rgba(7, 7, 7, 0.08)",
                                                    width: 600,
                                                }}
                                            >
                                                <ClickAwayListener onClickAway={handleClose}>
                                                    <Box>
                                                        <Grid container spacing={2}>

                                                            <Grid size={6}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Category</Typography>
                                                                <Select
                                                                    name="StatutoryTrackerType"
                                                                    options={categoryOptions}
                                                                    placeholder="Select Category "
                                                                    onChange={(option) => handleSelectChange(option, "StatutoryTrackerType")}
                                                                    value={categoryOptions.find(option => option.value === filters.StatutoryTrackerType) || null}
                                                                />
                                                            </Grid>
                                                            <Grid size={6}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Phase</Typography>
                                                                <Select
                                                                    name="Phase"
                                                                    options={phaseOptions}
                                                                    placeholder="Select Phase"
                                                                    onChange={(option) => handleSelectChange(option, "Phase")}
                                                                    value={phaseOptions.find(option => option.value === filters.Phase) || null}
                                                                />
                                                            </Grid>

                                                            <Grid size={6}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Status</Typography>
                                                                <Select
                                                                    name="Status"
                                                                    options={statusOptions}
                                                                    placeholder="Select Status"
                                                                    onChange={(option) => handleSelectChange(option, "Status")}
                                                                    value={statusOptions.find(option => option.value === filters.Status) || null}
                                                                />
                                                            </Grid>
                                                            <Grid size={6}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select SPOC</Typography>
                                                                <Select
                                                                    name="SPOC"
                                                                    options={spocOptions}
                                                                    placeholder="Select SPOC"
                                                                    onChange={(option) => handleSelectChange(option, "SPOC")}
                                                                    value={spocOptions.find(option => option.value === filters.SPOC) || null}
                                                                />
                                                            </Grid>


                                                            <Grid size={6}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Responsible</Typography>
                                                                <Select
                                                                    name="Responsibility"
                                                                    options={contactListRes}
                                                                    placeholder="Select EntityResponsibe"
                                                                    onChange={(option) => handleSelectChange(option, "Responsibility")}
                                                                    value={contactListRes.find(option => option.value === filters.Responsibility) || null}
                                                                />
                                                            </Grid>
                                                            <Grid size={6}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>From TargetDate</Typography>
                                                                <TextField
                                                                    type="date"
                                                                    name="FromDate"
                                                                    value={filters.FromDate ? new Date(filters.FromDate).toISOString().split('T')[0] : ""}
                                                                    // onBlur={handleBlur}
                                                                    onChange={handleDateChange}
                                                                    onClick={(e) => {
                                                                        // Trigger the native date picker
                                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                                        if (input) input.showPicker?.();
                                                                    }}
                                                                    fullWidth
                                                                    sx={{
                                                                        // ...rightTextStyle,
                                                                        "& input::-webkit-calendar-picker-indicator": {
                                                                            display: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& input[type='date']": {
                                                                            appearance: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            "& fieldset": {
                                                                                border: "none",
                                                                            },
                                                                        },
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                                            >
                                                                                {calenderSVG}
                                                                            </Box>
                                                                        ),
                                                                        sx: {
                                                                            backgroundColor: "#E7E7EB",
                                                                            height: "40px",
                                                                            width: { xs: "100%", md: "100%" },
                                                                            borderRadius: "3px",
                                                                        },
                                                                    }}
                                                                />
                                                            </Grid>
                                                            <Grid size={6}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>To TargetDate</Typography>
                                                                <TextField
                                                                    type="date"
                                                                    name="ToDate"
                                                                    value={filters.ToDate ? new Date(filters.ToDate).toISOString().split('T')[0] : ""}
                                                                    // onBlur={handleBlur}
                                                                    onChange={handleDateChange}
                                                                    onClick={(e) => {
                                                                        // Trigger the native date picker
                                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                                        if (input) input.showPicker?.();
                                                                    }}
                                                                    fullWidth
                                                                    sx={{
                                                                        // ...rightTextStyle,
                                                                        "& input::-webkit-calendar-picker-indicator": {
                                                                            display: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& input[type='date']": {
                                                                            appearance: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            "& fieldset": {
                                                                                border: "none",
                                                                            },
                                                                        },
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                                            >
                                                                                {calenderSVG}
                                                                            </Box>
                                                                        ),
                                                                        sx: {
                                                                            backgroundColor: "#E7E7EB",
                                                                            height: "40px",
                                                                            width: { xs: "100%", md: "100%" },
                                                                            borderRadius: "3px",
                                                                        },
                                                                    }}
                                                                />
                                                            </Grid>

                                                            <Grid size={12}>
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        justifyContent: "flex-end",
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    <Button onClick={handleReset}
                                                                        sx={{
                                                                            textTransform: "none",
                                                                            color: "#F15744",
                                                                            fontSize: "14px",
                                                                            lineHeight: "100%",
                                                                            fontFamily: "Outfit, sans-serif",
                                                                            borderRadius: "3px",
                                                                            fontWeight: 500,
                                                                            px: "20px",
                                                                            py: "15px",
                                                                        }}>
                                                                        Reset
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        onClick={handleApplyFilters}
                                                                        variant="contained"
                                                                        sx={{
                                                                            backgroundColor: "#2563EB",
                                                                            borderRadius: "3px",
                                                                            textTransform: "none",
                                                                            fontWeight: 500,
                                                                            fontSize: "14px",
                                                                            lineHeight: "100%",
                                                                            fontFamily: "Outfit, sans-serif",
                                                                            color: "#FFFFFF",
                                                                            px: "20px",
                                                                            py: "15px",
                                                                        }}
                                                                    >
                                                                        Apply
                                                                    </Button>
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </ClickAwayListener>
                                            </Paper>
                                        </Grow>
                                    )}
                                </Popper>

                                <IconButton onClick={() =>
                                    handleExport(
                                        filters,
                                        exportStatutoryApprovalData
                                    )
                                } disabled={isExporting}>
                                    <MdOutlineFileDownload size={24} color="#48535B" />
                                </IconButton>

                                {/* <IconButton>
                                    <BiListUl size={24} color="#48535B" />
                                </IconButton> */}
                                {/* <IconButton>
                                    <MdOutlineFileDownload size={24} color="#48535B" />
                                </IconButton> */}
                                <IconButton
                                    onClick={() => trigger(filters)}
                                >
                                    <LuRefreshCw size={20} color="#48535B" />
                                </IconButton>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>


                <Grid size={12}>
                    <TableComponent
                        data={approvalList?.data || []}
                        isLoading={isFetching}
                        columns={columns}
                        order={sortOrder}
                        orderBy={sortBy}
                        createSortHandler={handleRequestSort}
                    />
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={Boolean(menuAnchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                mt: 1,
                                borderRadius: "3px",
                                minWidth: 140,
                                border: "1px solid #E5E9EB",
                                boxShadow: "0px 10px 30px rgba(15, 23, 42, 0.12)",
                                overflow: "hidden",
                            },
                        }}
                        MenuListProps={{
                            sx: {
                                py: 0.5,
                            },
                        }}
                    >
                        {viewAccess &&
                            <MenuItem
                                onClick={handleMenuView}
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: "#111827",
                                    "&:hover": {
                                        backgroundColor: "#F6F8F9",
                                    },
                                }}
                            >
                                <LuEye size={16} style={{ color: "#4B5563" }} />
                                <span>View</span>
                            </MenuItem>
                        }

                        {editAccess &&
                            <MenuItem
                                onClick={handleMenuEdit}
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: "#111827",
                                    "&:hover": {
                                        backgroundColor: "#F6F8F9",
                                    },
                                }}
                            >
                                <EditIcon style={{ width: 14, height: 14 }} />
                                <span>Edit</span>
                            </MenuItem>
                        }

                    </Menu>

                    <CustomPagination
                        totalCount={approvalList?.totalRecords || 0}
                        pageSize={pageSize}
                        page={pageNumber}
                        onPageChange={handlePageChange}
                        setCurrentPage={setPageNumber}
                        onPageSizeChange={handlePageSizeChange}
                    />
                </Grid>
            </Grid>


            {isAddApproval && <AddApproval open={isAddApproval} handleClose={handleAddSApproval} refetchCounts={refetchCounts} />}
            {isEdit && <EditApproval open={isEdit} handleClose={handleEditRiskClose} approvalData={selectedData} refetchCounts={refetchCounts} />}
            {isView && <ViewApproval open={isView} handleClose={handleViewClose} rowData={viewData} />}
        </>
    );
}

export default ApprovalTracker;
